import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Copy, ExternalLink, Loader2, XCircle } from "lucide-react";
import { Link } from "@/components/ui/link";
import { usePathname } from "@/lib/navigation";
import { withReturnTo } from "@/lib/return-to";
import type { KnowledgeBaseView } from "@/types/api";
import * as React from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button, Input, Label } from "@/components/ui/primitives";
import { toast } from "@/components/ui/toaster";
import {
  checkKbConnection,
  checkKbDocumentSource,
  createKnowledgeBase,
  getKbAwsOnboarding,
  listMyDomains,
  type KbAwsOnboarding,
  type KnowledgeBaseInput,
} from "@/lib/api/endpoints";
import { useEmbeddingModels, useVectorStores } from "@/lib/query/hooks";
import { qk } from "@/lib/query/keys";

const selectClass =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

// The embedding model is chosen from the tenant's configured connections
// rather than typed here, so the API key is entered once under Embedding
// models rather than per knowledge base.
type CheckState = { status: "idle" | "checking" | "ok" | "fail"; error?: string };

/** Register a new SQL knowledge base: source → embeddings → vector store. */
export function KbRegisterDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The new KB, once created. Lets the caller route onward — a fresh KB is
   *  not queryable until its schema is ingested, so "created" is the middle of
   *  the job, not the end of it. */
  onCreated?: (kb: KnowledgeBaseView) => void;
}) {
  // Wherever this dialog was opened from is where the admin should land again
  // after adding the embedding model they were sent away for.
  const currentPath = usePathname();
  const qc = useQueryClient();
  const domains = useQuery({ queryKey: qk.myDomains, queryFn: listMyDomains, enabled: open });

  const [domainSlug, setDomainSlug] = React.useState("");
  const [name, setName] = React.useState("");
  // Which kind of source this KB reads. Chosen up front because it decides
  // which credentials are even meaningful (ADR-019).
  const [sourceType, setSourceType] = React.useState<"sql" | "document">("sql");
  const [sourceDsn, setSourceDsn] = React.useState("");

  // Document source — the customer's own bucket. We never copy documents into
  // storage we own, so this is read access to their prefix.
  const [bucket, setBucket] = React.useState("");
  const [prefix, setPrefix] = React.useState("");
  const [region, setRegion] = React.useState("");
  const [roleArn, setRoleArn] = React.useState("");
  // The ExternalId is *ours*, generated per tenant (ADR-019), so it is fetched
  // and displayed rather than collected. Uniqueness across tenants is what
  // makes it a confused-deputy defence, and that is not an invariant an
  // operator typing into a box can hold.
  const onboarding = useQuery({
    queryKey: qk.kbAwsOnboarding,
    queryFn: getKbAwsOnboarding,
    enabled: open,
  });
  const externalId = onboarding.data?.external_id ?? "";
  const [allowedTables, setAllowedTables] = React.useState("");
  const [embeddingModelSlug, setEmbeddingModelSlug] = React.useState("");
  const embeddingModels = useEmbeddingModels();
  const [vectorStoreSlug, setVectorStoreSlug] = React.useState("");
  const vectorStores = useVectorStores();
  const [vectorCollection, setVectorCollection] = React.useState("");
  const [check, setCheck] = React.useState<CheckState>({ status: "idle" });

  React.useEffect(() => {
    if (!open) return;
    setDomainSlug("");
    setName("");
    setSourceDsn("");
    setAllowedTables("");
    setEmbeddingModelSlug("");
    setVectorStoreSlug("");
    setVectorCollection("");
    setCheck({ status: "idle" });
  }, [open]);

  // One store is the common case; don't make the admin pick it.
  React.useEffect(() => {
    const only = vectorStores.data?.length === 1 ? vectorStores.data[0] : undefined;
    if (open && !vectorStoreSlug && only) setVectorStoreSlug(only.slug);
  }, [open, vectorStoreSlug, vectorStores.data]);

  React.useEffect(() => {
    const first = domains.data?.[0];
    if (open && !domainSlug && first) setDomainSlug(first.slug);
  }, [open, domainSlug, domains.data]);

  // A changed DSN invalidates a previous check result.
  const onDsnChange = (v: string) => {
    setSourceDsn(v);
    setCheck({ status: "idle" });
  };

  const testConn = useMutation({
    mutationFn: () =>
      sourceType === "document"
        ? checkKbDocumentSource(documentSource())
        : checkKbConnection(sourceDsn.trim()),
    onMutate: () => setCheck({ status: "checking" }),
    onSuccess: (r) =>
      setCheck(r.ok ? { status: "ok" } : { status: "fail", error: r.error ?? "Connection failed" }),
    onError: (e: Error) => setCheck({ status: "fail", error: e.message }),
  });

  const parseTables = (): string[] | null => {
    const items = allowedTables
      .split(/[\n,]/)
      .map((s) => s.trim())
      .filter(Boolean);
    return items.length ? items : null;
  };

  const create = useMutation({
    mutationFn: () => {
      const isDocument = sourceType === "document";
      const body: KnowledgeBaseInput = {
        domain_slug: domainSlug,
        name: name.trim(),
        source_type: sourceType,
        // Each source type sends only its own config — the API refuses a body
        // carrying both, and the database CHECK backs that up.
        source_dsn: isDocument ? null : sourceDsn.trim(),
        document_source: isDocument ? documentSource() : null,
        embedding_model_slug: embeddingModelSlug,
        vector_store_slug: vectorStoreSlug,
        vector_collection: vectorCollection.trim(),
        allowed_tables: isDocument ? null : parseTables(),
      };
      return createKnowledgeBase(body);
    },
    onSuccess: async (kb) => {
      toast.success(
        sourceType === "document"
          ? "Knowledge base created. Upload or sync documents, then ingest."
          : "Knowledge base created. Ingest its schema to make it queryable.",
      );
      await qc.invalidateQueries({ queryKey: qk.knowledgeBases });
      onOpenChange(false);
      onCreated?.(kb);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const documentSource = () => ({
    bucket: bucket.trim(),
    prefix: prefix.trim(),
    region: region.trim() || null,
    role_arn: roleArn.trim() || null,
    external_id: externalId.trim() || null,
  });

  const noDomains = domains.data?.length === 0;
  const sourceReady = sourceType === "document" ? !!bucket.trim() : !!sourceDsn.trim();
  const canSubmit =
    !!name.trim() &&
    !!domainSlug &&
    sourceReady &&
    embeddingModelSlug !== "" &&
    vectorStoreSlug !== "" &&
    !!vectorCollection.trim() &&
    !create.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Register knowledge base</DialogTitle>
          <DialogDescription>
            Connect a SQL database, embed its schema into a vector store, then ask questions in
            plain English. Credentials are stored encrypted and never shown again.
          </DialogDescription>
        </DialogHeader>

        {noDomains ? (
          <p className="text-sm text-muted-foreground">Create a domain first.</p>
        ) : (
          <form
            className="space-y-5"
            onSubmit={(e) => {
              e.preventDefault();
              if (canSubmit) create.mutate();
            }}
          >
            {/* --- Identity --- */}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="kb-name">Name</Label>
                <Input
                  id="kb-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Sales DB"
                  autoFocus
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="kb-domain">Domain</Label>
                <select
                  id="kb-domain"
                  className={selectClass}
                  value={domainSlug}
                  onChange={(e) => setDomainSlug(e.target.value)}
                >
                  {(domains.data ?? []).map((d) => (
                    <option key={d.slug} value={d.slug}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* --- Source --- */}
            <fieldset className="space-y-3 rounded-md border border-border p-4">
              <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Source
              </legend>

              <div className="space-y-1.5">
                <Label htmlFor="kb-source-type">Source type</Label>
                <select
                  id="kb-source-type"
                  className={selectClass}
                  value={sourceType}
                  onChange={(e) => {
                    setSourceType(e.target.value as "sql" | "document");
                    // A check result belongs to the source it was run against.
                    setCheck({ status: "idle" });
                  }}
                >
                  <option value="sql">SQL database — ask questions of your data</option>
                  <option value="document">Documents — PDF, Word, Markdown, text, JSON</option>
                </select>
              </div>

              {sourceType === "document" ? (
                <DocumentSourceFields
                  bucket={bucket}
                  setBucket={setBucket}
                  prefix={prefix}
                  setPrefix={setPrefix}
                  region={region}
                  setRegion={setRegion}
                  roleArn={roleArn}
                  setRoleArn={setRoleArn}
                  onboarding={onboarding.data ?? null}
                  onboardingError={onboarding.isError ? (onboarding.error as Error).message : null}
                  check={check}
                  onTest={() => testConn.mutate()}
                  onChanged={() => setCheck({ status: "idle" })}
                />
              ) : (
                <>
                  <div className="space-y-1.5">
                    <Label htmlFor="kb-dsn">Connection string (DSN)</Label>
                    <div className="flex gap-2">
                      <Input
                        id="kb-dsn"
                        type="password"
                        value={sourceDsn}
                        onChange={(e) => onDsnChange(e.target.value)}
                        placeholder="postgresql://readonly:pw@host:5432/sales"
                        autoComplete="off"
                        className="font-mono text-xs"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => testConn.mutate()}
                        disabled={!sourceDsn.trim() || check.status === "checking"}
                      >
                        {check.status === "checking" ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          "Test"
                        )}
                      </Button>
                    </div>
                    {check.status === "ok" ? (
                      <p className="flex items-center gap-1.5 text-xs text-[hsl(var(--success))]">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Connection succeeded.
                      </p>
                    ) : check.status === "fail" ? (
                      <p className="flex items-start gap-1.5 text-xs text-destructive">
                        <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {check.error}
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        Use a read-only role — the KB only ever issues guarded <code>SELECT</code>s.
                      </p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="kb-tables">Allowed tables</Label>
                    <textarea
                      id="kb-tables"
                      className="flex min-h-[64px] w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      value={allowedTables}
                      onChange={(e) => setAllowedTables(e.target.value)}
                      placeholder="customers, orders, order_items"
                    />
                    <p className="text-xs text-muted-foreground">
                      Comma- or newline-separated tables in the <code>public</code> schema. Leave
                      blank to include every table (not recommended for large or sensitive schemas).
                    </p>
                    <p className="text-xs text-muted-foreground">
                      This scope narrows what the assistant may query, but the database role&apos;s
                      grants are the real boundary — connect with a role that can only{" "}
                      <code>SELECT</code> the tables listed here.
                    </p>
                  </div>
                </>
              )}
            </fieldset>

            {/* --- Embeddings --- */}
            <fieldset className="space-y-3 rounded-md border border-border p-4">
              <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Embeddings
              </legend>
              <div className="space-y-1.5">
                <Label htmlFor="kb-model">Embedding model</Label>
                <select
                  id="kb-model"
                  className={selectClass}
                  value={embeddingModelSlug}
                  onChange={(e) => setEmbeddingModelSlug(e.target.value)}
                >
                  <option value="">Select a configured model…</option>
                  {(embeddingModels.data ?? []).map((m) => (
                    <option key={m.slug} value={m.slug}>
                      {m.name} — {m.model}
                    </option>
                  ))}
                </select>
              </div>
              {embeddingModels.data?.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  None configured yet —{" "}
                  <Link
                    href={withReturnTo("/embedding-models", currentPath)}
                    className="underline underline-offset-4"
                  >
                    add one first
                  </Link>
                  .
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Its key is also used to generate SQL from your questions at query time. The model
                  is locked once ingested — changing it later forces a re-ingest.
                </p>
              )}
            </fieldset>

            {/* --- Vector store --- */}
            <fieldset className="space-y-3 rounded-md border border-border p-4">
              <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Vector store
              </legend>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="kb-vstore">Store</Label>
                  <select
                    id="kb-vstore"
                    className={selectClass}
                    value={vectorStoreSlug}
                    onChange={(e) => setVectorStoreSlug(e.target.value)}
                  >
                    <option value="">Select a vector store…</option>
                    {(vectorStores.data ?? []).map((s) => (
                      <option key={s.slug} value={s.slug}>
                        {s.name} — {s.provider}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="kb-coll">Collection</Label>
                  <Input
                    id="kb-coll"
                    value={vectorCollection}
                    onChange={(e) => setVectorCollection(e.target.value)}
                    placeholder="sales_kb"
                    className="font-mono text-xs"
                  />
                </div>
              </div>
              {vectorStores.data?.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  None registered yet —{" "}
                  <Link
                    href={withReturnTo("/vector-stores", currentPath)}
                    className="underline underline-offset-4"
                  >
                    add one first
                  </Link>
                  .
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  The store&apos;s URL and key are managed under Vector stores. The collection is
                  this knowledge base&apos;s own; several knowledge bases can share a store.
                </p>
              )}
            </fieldset>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={!canSubmit}>
                {create.isPending ? "Creating…" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** The half of the cross-account trust that lives in the customer's account.
 *
 * These are facts about *Aviary's* side (ADR-019) — which principals will call
 * AssumeRole, what IAM path the role must sit at — so the operator cannot
 * discover any of it from inside their own AWS console. Showing them here, next
 * to the fields they feed, is what replaces a support thread.
 */
function AwsTrustInstructions({
  onboarding,
  error,
}: {
  onboarding: KbAwsOnboarding | null;
  error: string | null;
}) {
  // Naming the endpoint matters: the usual cause is a control-plane database
  // behind its migrations, which is invisible from the browser.
  if (error) {
    return (
      <p className="rounded-md border border-dashed border-destructive/50 p-3 text-xs text-destructive">
        Could not load your AWS onboarding details ({error}). The knowledge base cannot be
        registered with a role until this resolves — check that the control plane is migrated (
        <code className="font-mono">migrate-control</code>) and that{" "}
        <code className="font-mono">/api/admin/my/kb-aws-onboarding</code> responds.
      </p>
    );
  }
  if (!onboarding) return null;

  // No principals configured means this deployment cannot do cross-account KBs
  // at all. Saying so beats rendering instructions that cannot be followed.
  if (onboarding.principal_arns.length === 0) {
    return (
      <p className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
        This Aviary deployment has no cross-account principals configured, so a role-based bucket
        cannot be set up from here. Ask your platform operator to set
        <code className="mx-1 font-mono">AVIARY_KB_ASSUME_ROLE_PRINCIPAL_ARNS</code>.
      </p>
    );
  }

  const launchUrl = onboarding.template_url
    ? `https://console.aws.amazon.com/cloudformation/home#/stacks/create/review?templateURL=${encodeURIComponent(
        onboarding.template_url,
      )}&stackName=aviary-kb-role&param_ExternalId=${encodeURIComponent(
        onboarding.external_id,
      )}&param_AviaryPrincipalArns=${encodeURIComponent(
        onboarding.principal_arns.join(","),
      )}&param_RolePath=${encodeURIComponent(onboarding.role_path)}`
    : null;

  return (
    <div className="space-y-2 rounded-md border bg-muted/30 p-3">
      <p className="text-xs font-medium">In your AWS account</p>

      {launchUrl ? (
        <p className="text-xs text-muted-foreground">
          <a
            className="inline-flex items-center gap-1 font-medium text-foreground underline"
            href={launchUrl}
            target="_blank"
            rel="noreferrer noopener"
          >
            Create the role with CloudFormation <ExternalLink className="h-3 w-3" />
          </a>{" "}
          — pre-filled with the values below. Paste the stack&apos;s RoleArn output above.
        </p>
      ) : null}

      <div className="space-y-1">
        <p className="text-xs text-muted-foreground">
          Trust these principals. Both are needed: the first runs the test above, the second does
          the ingesting.
        </p>
        <ul className="space-y-0.5">
          {onboarding.principal_arns.map((arn) => (
            <li key={arn} className="break-all font-mono text-[11px] text-foreground">
              {arn}
            </li>
          ))}
        </ul>
      </div>

      <p className="text-xs text-muted-foreground">
        Create the role at path <code className="font-mono">{onboarding.role_path}</code> — Aviary
        grants itself access by path, so another path fails with AccessDenied. Require the External
        ID with an <code className="font-mono">sts:ExternalId</code> condition: without that
        condition AWS ignores the value rather than rejecting it, so the role would appear to work
        while any tenant could reach your bucket.
      </p>
    </div>
  );
}

/** The document-source half of the form.
 *
 * Split out rather than inlined because it is a different conversation from a
 * DSN: the operator is granting cross-account access in *their* AWS account,
 * and the fields only make sense with an explanation attached. Extracting it
 * keeps the dialog's own logic readable.
 */
function DocumentSourceFields({
  bucket,
  setBucket,
  prefix,
  setPrefix,
  region,
  setRegion,
  roleArn,
  setRoleArn,
  onboarding,
  onboardingError,
  check,
  onTest,
  onChanged,
}: {
  bucket: string;
  setBucket: (v: string) => void;
  prefix: string;
  setPrefix: (v: string) => void;
  region: string;
  setRegion: (v: string) => void;
  roleArn: string;
  setRoleArn: (v: string) => void;
  onboarding: KbAwsOnboarding | null;
  /** Non-null when the lookup failed. Kept distinct from "not here yet": an
   *  error rendered as a pending state is a field that says "loading…" for
   *  ever, which is exactly what the operator cannot debug. */
  onboardingError: string | null;
  check: CheckState;
  onTest: () => void;
  onChanged: () => void;
}) {
  // Any edit invalidates a previous result — a green tick against different
  // settings than the ones about to be saved is worse than no tick.
  const edit = (setter: (v: string) => void) => (value: string) => {
    setter(value);
    onChanged();
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Aviary reads documents from <span className="font-medium text-foreground">your own</span> S3
        bucket and never stores copies. Grant read access to the prefix below; add write on it too
        if you want to upload from this console.
      </p>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="kb-bucket">Bucket</Label>
          <Input
            id="kb-bucket"
            value={bucket}
            onChange={(e) => edit(setBucket)(e.target.value)}
            placeholder="acme-knowledge"
            className="font-mono text-xs"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="kb-prefix">Prefix</Label>
          <Input
            id="kb-prefix"
            value={prefix}
            onChange={(e) => edit(setPrefix)(e.target.value)}
            placeholder="handbook/"
            className="font-mono text-xs"
          />
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="kb-region">Region</Label>
          <Input
            id="kb-region"
            value={region}
            onChange={(e) => edit(setRegion)(e.target.value)}
            placeholder="eu-west-2"
            className="font-mono text-xs"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="kb-external-id">External ID</Label>
          <div className="flex gap-2">
            <Input
              id="kb-external-id"
              value={onboarding?.external_id ?? ""}
              readOnly
              placeholder={onboardingError ? "unavailable" : onboarding ? "" : "loading…"}
              className="font-mono text-xs"
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              title="Copy External ID"
              disabled={!onboarding}
              onClick={() => {
                if (onboarding) {
                  void navigator.clipboard.writeText(onboarding.external_id);
                  toast.success("External ID copied.");
                }
              }}
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          {onboardingError ? (
            <p className="flex items-start gap-1.5 text-xs text-destructive">
              <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              {onboardingError}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              Generated for your tenant — paste it into the role&apos;s trust policy.
            </p>
          )}
        </div>
      </div>

      <AwsTrustInstructions onboarding={onboarding} error={onboardingError} />

      <div className="space-y-1.5">
        <Label htmlFor="kb-role-arn">Role ARN</Label>
        <div className="flex gap-2">
          <Input
            id="kb-role-arn"
            value={roleArn}
            onChange={(e) => edit(setRoleArn)(e.target.value)}
            placeholder="arn:aws:iam::123456789012:role/AviaryRead"
            className="font-mono text-xs"
          />
          <Button
            type="button"
            variant="outline"
            onClick={onTest}
            disabled={!bucket.trim() || check.status === "checking"}
          >
            {check.status === "checking" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Test"}
          </Button>
        </div>
        {check.status === "ok" ? (
          <p className="flex items-start gap-1.5 text-xs text-[hsl(var(--success))]">
            <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            {check.error ?? "Bucket reachable and readable."}
          </p>
        ) : check.status === "fail" ? (
          <p className="flex items-start gap-1.5 text-xs text-destructive">
            <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {check.error}
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">
            Test before saving: this role lives in your AWS account, so a mistake here surfaces as
            an empty knowledge base later. A trust policy that names only the first principal will
            pass this check and then fail at ingest — grant both.
          </p>
        )}
      </div>
    </div>
  );
}
